import * as mwx from '@/utils/common.js';
import { addTemplateType } from '@/service/skuManage.js';
import Ajax from './Ajax';
import { isDebuggerProduction, isMockData, platform } from './config';
import Store from './Store';
import { urlFilenameMap } from './service.js';
import { saveImageDataFontListToStore } from '@/utils/editImage/imageDataFontUtil.js';
const mockdata = require('./mockdata.js');

let apiCache = {};
async function cacheApi(cacheKey, fn) {
  if (apiCache[cacheKey]) {
    return apiCache[cacheKey];
  } else {
    let r = await fn();
    apiCache[cacheKey] = r;
    return r;
  }
}

function queryString(data) {
  return Object.keys(data || {})
    .filter(
      (key) => data[key] !== undefined && data[key] !== null && data[key] !== ''
    )
    .map((key) => `${key}=${encodeURIComponent(data[key])}`)
    .join('&');
}

function handleFormData(data) {
  let formData = new FormData();
  Object.keys(data).forEach((key) => {
    if (data[key] !== undefined && data[key] !== 'undefined') {
      formData.append(key, data[key]);
    }
  });
  return formData;
}

/**
 * 存储 url-filename 映射
 * @param {Object} res - 接口返回的数据对象
 */
function storeUrlFilenameMap(res) {
  if (res && res.url && res.fileName) {
    urlFilenameMap[res.url] = res.fileName;
  }
}

const SparkMD5 = require('spark-md5');
function getMd5(file) {
  return new Promise((resolve, reject) => {
    const spark = new SparkMD5.ArrayBuffer();
    const reader = new FileReader();
    reader.onload = function (e) {
      spark.append(e.target.result);
      const md5Hash = spark.end();
      resolve(md5Hash);
    };
    reader.readAsArrayBuffer(file);
  });
}

/**
 * 检测图片安全性，优先通过 mediaId 触发异步检测，退化到 fileName 方式
 * 轮询远端结果，超时 60 秒返回 -1，1009 返回 -2，其余返回接口响应
 * @param {Object} r 图片检测入参，包含 mediaId 或 fileName
 * @returns {Promise<number|Object>} -2：媒体库问题，-1：超时/异常，其余为接口响应
 */
export function checkImageSafe(r) {
  let timeout = 60;
  async function fn(timer) {
    let res;
    try {
      res = await (r.mediaId
        ? v5MediaCheckAsync({ mediaId: r.mediaId }, { disableErrorToast: true })
        : v5ImageCheckAsync(
            { fileName: r.fileName },
            { disableErrorToast: true }
          ));
      timer && clearInterval(timer);
      return res;
    } catch (error) {
      if (error && error.code == 1009) {
        console.log('图片检测1009：', r.fileName);
        timer && clearInterval(timer);
        return -2;
      } else {
        return -1;
      }
    }
  }
  return new Promise(async (resolve, reject) => {
    let timer = null;
    let timer2 = setTimeout(() => {
      if (timer) {
        clearInterval(timer);
      }
      // 超时，返回结果 -1
      resolve(-1);
    }, timeout * 1000);
    timer = setInterval(async () => {
      try {
        let t = await fn(timer);
        if (t != -1) {
          clearInterval(timer2);
          clearInterval(timer);
          // 检测完成
          resolve(t);
        }
      } catch (error) {
        clearInterval(timer2);
        clearInterval(timer);
        // 错误时返回 -1
        resolve(-1);
      }
    }, 500);
    try {
      let t = await fn(timer);
      if (t != -1) {
        clearInterval(timer2);
        clearInterval(timer);
        // 检测完成
        resolve(t);
      }
    } catch (error) {
      clearInterval(timer2);
      clearInterval(timer);
      // 错误时返回 -1
      resolve(-1);
    }
  });
}

// 图片安全检测任务缓存（内存存储 Promise）
const imageSafeCheckCache = new Map();

// 获取缓存 key
function getSafeCheckCacheKey(fileName, mediaId) {
  if (mediaId) {
    return `media_${mediaId}`;
  } else if (fileName) {
    return `image_${fileName}`;
  } else {
    throw new Error('fileName 和 mediaId 至少需要提供一个');
  }
}

// 是否需要base64格式上传图片, 如果上传失败报empty_file，则改为base64格式上传
let isNeedBase64Upload = false;
// 以下设备需要通过base64格式上传图片,file格式会报错
let isErrorUpload =
  /iPhone/i.test(navigator.userAgent) &&
  /AlipayWallet|UCBrowser|Quark/i.test(navigator.userAgent);

let blobUrlCacheList = [];

// 处理日志文件函数
async function getLogFileUploadFormData(file) {
  let isFile = true;
  if (isFile && (isErrorUpload || isNeedBase64Upload)) {
    isFile = false;
    file = await mwx.fileToBase64(file);
  }
  let formData = {
    file,
    mediaName: file.name,
    poi: 0,
    mediaType: 'log',
    temporary: 0,
    fileSize: file.size
  };
  return { formData, isFile };
}

async function getImageUploadFormData(file, opt) {
  let check = opt.riskyCheck || 0; // 关闭图片安全检测
  let isFile = typeof file === 'string' ? false : true;
  let isBlobUrl = !isFile && /^blob:/.test(file);
  let isBase64Url = !isFile && /^data:/.test(file);

  let timestamp = opt.timestamp;
  let resolveCallback;
  function getMb(file) {
    return Math.ceil(file.size / 1024 / 1024);
  }

  function getLogFile(file) {
    if (!file) {
      return 'file 不存在';
    }
    return isFile
      ? `mb=${getMb(file)},fileName=${file.name},type=${file.type}`
      : file.slice(0, 20);
  }

  let logStr = `isBlobUrl=${isBlobUrl},timestamp=${timestamp}, checkImage 1:,isFile=${isFile},file=${getLogFile(
    file
  )}`;

  if (isFile) {
    logStr += `[isFile],mb=${getMb(file)}`;
  }
  if (isBase64Url) {
    let beforeMb = Math.ceil(file.length / 1024 / 1024);
    logStr += `[isBase64Url]: beforeMb=${beforeMb}`;
    file = await mwx.base64ToFile(file);
    logStr += `[base64ToFile after]: mb=${Math.ceil(file.size / 1024 / 1024)}`;
    isFile = true;
  }

  if (isBlobUrl) {
    if (window.__fileUrlMap && window.__fileUrlMap[file]) {
      logStr += `__fileUrlMap[file]文件存在`;
      let blobFileUrl = file;
      resolveCallback = () => {
        blobUrlCacheList.push(blobFileUrl);
        if (blobUrlCacheList.length > 10) {
          let url = blobUrlCacheList.shift();
          mwx.revokeBlobUrl(url);
        }
        // 待优化: blobUrl存在无法访问情况,这里先延迟清除
        setTimeout(() => {
          mwx.revokeBlobUrl(blobFileUrl);
        }, 60 * 1000);
      };
      file = window.__fileUrlMap[blobFileUrl];
      isFile = true;
    } else {
      try {
        file = await mwx.blobUrlToFile(file);
        logStr += `blobUrlToFile生成文件存在`;
        isFile = true;
      } catch (error) {
        mwx.log.error('blobUrl:' + String(file));
        mwx.log.error('blobUrlToFile fail');
        mwx.log.error(error);
        throw error;
      }
    }
    logStr += `[isBlobUrl]:,file=${getLogFile(file)}`;
  }

  // mwx.log.info(logStr);
  let cardInfo = mwx.action.getCardInfo() || {};
  let meta = '';
  if (cardInfo && cardInfo.name) {
    meta = cardInfo.name + ':' + (cardInfo.w + '*' + cardInfo.h);
  }
  if (isFile && (isErrorUpload || isNeedBase64Upload)) {
    isFile = false;
    file = await mwx.fileToBase64(file);
  }
  if (opt.permanent == 1) {
    let wxData = mwx.getWxData();
    if (wxData && wxData.isGc == 1) {
      opt.poi = 4000; // 谷仓图片上传
    }
  }

  let formData = {
    [opt.permanent == 1 || !isFile ? 'file' : 'name']: file,
    // name: 'name',
    poi: opt.poi || 306,
    source: 2,
    meta: meta || '',
    riskyCheck: check ? 2 : 0,
    originFileNameList: opt.originFileNameList
      ? JSON.stringify(opt.originFileNameList)
      : '[]',
    origin: opt.origin || 0,
    ...(opt.addData ? opt.addData : {})
  };

  // 图片文件
  if (opt.permanent == 1) {
    let mediaType = 'image';
    try {
      mediaType = file.type.replace('image/', '');
      if (['jpg', 'jpeg', 'png', 'tiff'].indexOf(mediaType) == -1) {
        mediaType = 'image';
      }
    } catch (error) {
      // 处理错误
    }
    formData = {
      ...formData,
      mediaType,
      temporary: 0,
      ignoreRisky: check ? 0 : 1
    };
  }
  if (!formData.width) {
    try {
      if (isFile) {
        let { width, height } = await mwx.getImageDimensions(file);
        formData.width = width;
        formData.height = height;
      } else {
        let imageInfo = await mwx.getImageInfo(file);
        formData.width = imageInfo.width;
        formData.height = imageInfo.height;
      }
    } catch (error) {
      console.error(error);
      // 处理错误
    }
  }

  return {
    formData,
    resolveCallback,
    file,
    isFile
  };
}

const isCacheCheckImage = false;

/**
 * 获取上传文件大小，单位 MB。
 * @param {*} file - 上传文件、Blob、base64 或普通字符串。
 * @returns {number|string} 可计算时返回向上取整后的 MB，无法计算时返回空字符串。
 */
function getUploadFileMb(file) {
  // File/Blob 场景优先使用 size 字段计算文件大小。
  if (file && file.size !== undefined) {
    return Math.ceil(file.size / 1024 / 1024);
  }

  // base64 或字符串上传场景按字符串长度估算文件大小。
  if (typeof file === 'string') {
    return Math.ceil(file.length / 1024 / 1024);
  }

  return '';
}

/**
 * 生成图片上传失败时的诊断日志信息。
 * @param {Object} params - 诊断参数。
 * @param {*} params.file - 当前上传文件、Blob、base64 或 URL。
 * @param {Object} params.formData - 上传接口表单数据。
 * @param {boolean} params.isFile - 当前是否为文件上传。
 * @param {string} params.api - 当前上传接口。
 * @param {Object} params.opt - 上传配置。
 * @param {*} params.err - 上传失败错误对象。
 * @param {string} params.errMsg - 已格式化的错误信息。
 * @param {number} params.repeatIndex - 当前重试次数。
 * @returns {Object} 上传失败诊断信息。
 */
function getUploadImageFailLogInfo({
  file,
  formData,
  isFile,
  api,
  opt,
  err,
  errMsg,
  repeatIndex
}) {
  let uploadFile =
    formData && formData.file !== undefined ? formData.file : file;
  let fileName = uploadFile && uploadFile.name;
  let type = uploadFile && uploadFile.type;
  let mediaType = formData && formData.mediaType;

  // formData 里有原始文件名时，补齐 File/Blob 之外的文件名信息。
  if (!fileName && formData) {
    fileName = formData.mediaName || formData.name || '';
  }

  // formData 里有 mediaType 时，补齐 File/Blob 之外的格式信息。
  if (!type && mediaType) {
    type = mediaType;
  }

  let format = type ? String(type).replace(/^image\//, '') : '';

  // 文件名包含扩展名时，作为格式信息的兜底来源。
  if (!format && fileName && fileName.indexOf('.') > -1) {
    format = fileName.split('.').pop();
  }

  let info = {
    api,
    isFile,
    permanent: opt.permanent,
    mediaType: opt.mediaType || mediaType || '',
    fileName,
    mb: getUploadFileMb(uploadFile),
    width: formData && formData.width,
    height: formData && formData.height,
    type,
    format,
    fileSize: uploadFile && uploadFile.size,
    errMsg,
    repeatIndex
  };

  // 错误对象存在时补充关键字段，便于区分网络错误、状态码和业务错误。
  if (err && typeof err === 'object') {
    info.errorCode = err.code;
    info.statusCode = err.statusCode;
    info.errorMessage = err.message;
    info.errorData = err.data;
  }

  return info;
}

/**
 * 判断上传接口是否返回 invalid_png
 * @param {*} err - Ajax catch 错误对象
 * @returns {boolean}
 */
function isInvalidPngError(err) {
  if (!err) {
    return false;
  }
  if (err === 'invalid_png' || err.data === 'invalid_png') {
    return true;
  }
  if (typeof err === 'string' && err.indexOf('invalid_png') > -1) {
    return true;
  }
  try {
    if (JSON.stringify(err).indexOf('invalid_png') > -1) {
      return true;
    }
  } catch (error) {}
  return false;
}

/**
 * 上传图片（内部实现）
 * @param {*} file   file对象, base64字符串, blobUrl字符串
 * @param {*} [opt={
 *    permanent: 是否永久上传
 *    riskyCheck: 是否进行风险检测
 *    disableErrorToast: 是否禁用错误提示
 *    addData: 额外数据
 * }]
 * @param {number} [repeatIndex=0] - 0 首次；1 表示已重试（empty_file / invalid_png）
 * @return {*}
 */
async function _checkImage(file, opt = {}, repeatIndex = 0) {
  let isLogFile = opt.mediaType == 'log';
  let resolveCallback;
  let hasRunResolveCallback = false;
  let uploadFailFormData;
  let uploadFailIsFile;
  let uploadFailApi = '';
  let hasLogUploadFailInfo = false;

  /**
   * 触发 fileToBlobUrl 的释放回调，确保成功、重试和失败路径只登记一次延迟释放。
   * @returns {void}
   */
  function runResolveCallbackOnce() {
    // 释放回调不存在或已触发时直接跳过，避免重复登记 revoke 定时器。
    if (!resolveCallback || hasRunResolveCallback) {
      return;
    }

    hasRunResolveCallback = true;
    resolveCallback();
  }

  /**
   * 记录上传失败时的文件诊断信息，避免重试或 throwError 路径重复写入。
   * @param {*} err - 上传失败错误对象。
   * @param {string} errMsg - 已格式化的错误信息。
   * @returns {void}
   */
  function logUploadImageFailInfo(err, errMsg) {
    try {
      // 日志文件上传失败不记录图片诊断信息，避免把 log 上传当成图片失败上报。
      if (isLogFile) {
        return;
      }

      // 已记录过诊断信息时直接跳过，避免同一次失败重复刷日志。
      if (hasLogUploadFailInfo) {
        return;
      }

      hasLogUploadFailInfo = true;
      mwx.log.error(
        'checkImage upload fail info',
        getUploadImageFailLogInfo({
          file,
          formData: uploadFailFormData,
          isFile: uploadFailIsFile,
          api: uploadFailApi,
          opt,
          err,
          errMsg,
          repeatIndex
        })
      );
    } catch (error) {
      console.error(error);
    }
  }

  try {
    let check = opt.riskyCheck || 0; // 图片安全检测开关
    let isFile = typeof file === 'string' ? false : true;
    let formData;
    let startTime = new Date();

    if (!file) {
      throw new Error('file is empty');
    } else if (isFile && file.size == 0) {
      throw new Error('file size is 0');
    }

    // 日志文件
    let timestamp = +new Date();
    opt.timestamp = timestamp;
    if (isLogFile) {
      let info = await getLogFileUploadFormData(file);
      formData = info.formData;
      isFile = info.isFile;
    } else {
      let info = await getImageUploadFormData(file, opt);
      formData = info.formData;
      resolveCallback = info.resolveCallback;
      file = info.file;
      isFile = info.isFile;
    }
    uploadFailFormData = formData;
    uploadFailIsFile = isFile;

    let digest = '';
    if (isFile) {
      try {
        digest = await Promise.race([
          getMd5(file),
          new Promise((resolve) => {
            setTimeout(() => {
              resolve('');
            }, 3000);
          })
        ]);
      } catch (error) {
        mwx.log.error(error);
        // 处理错误
      }
    }
    formData.digest = digest;

    let api =
      opt.permanent == 1
        ? isFile
          ? `/v5/media/upload?lang=zh`
          : `/v5/media/upload/base64?lang=zh`
        : isFile
        ? `/v5/image/upload?lang=zh`
        : `/v5/image/upload/base64?lang=zh`;
    api += `&timestamp=${timestamp}`;
    uploadFailApi = api;
    let data = handleFormData(formData);
    let beforeUploadTime = new Date();
    let checkImageSafeTime = 0; // 图片安全检测耗时
    return Ajax.post(api, data, {
      headers: {
        'Content-Type': 'multipart/form-data'
      },
      disableErrorToast: opt.disableErrorToast
    })
      .then(async (r) => {
        if (check) {
          // 后台启动检测任务，不阻塞返回
          let cacheKey = getSafeCheckCacheKey(r.fileName, r.mediaId);
          // 检查是否已有检测结果存储在 sessionStorage
          let cachedResult = Store.getSession(cacheKey);
          if (
            cachedResult !== undefined &&
            cachedResult !== null &&
            isCacheCheckImage
          ) {
            // 已有结果，不需要重复检测
            return r;
          }
          // 检查内存缓存，避免重复发起检测
          if (!imageSafeCheckCache.has(cacheKey)) {
            let checkPromise = checkImageSafe(r)
              .then((result) => {
                // 检测完成，存储结果到 sessionStorage
                Store.setSession(cacheKey, result);
                return result;
              })
              .catch((error) => {
                // 错误时也存储结果 -1
                Store.setSession(cacheKey, -1);
                return -1;
              });
            imageSafeCheckCache.set(cacheKey, checkPromise);
          }
          // 直接返回上传结果，不等待检测
          return r;
        } else {
          return r;
        }
      })
      .then((res) => {
        let returnData = {};
        mwx.logTime('checkImage耗时', new Date() - startTime + 'ms');
        runResolveCallbackOnce();
        try {
          if (res && !res.url) {
            mwx.slog.error('checkImage fail: ' + JSON.stringify(res));
          }
        } catch (error) {}
        if (res && (res.fileName || opt.permanent == 1)) {
          if (mwx.isDev && /^https:\/\//.test(location.href)) {
            res.url = res.url.replace(/^https/, 'http');
          }
          // 存储 url-filename 映射
          storeUrlFilenameMap(res);
          returnData = { ...res, checkStatus: 1, errMsg: '图片链接不存在' };
        } else if (res == 1009) {
          returnData = { checkStatus: -1, errMsg: '图片检测异常' };
        } else {
          mwx.log.error('checkImage fail: ' + JSON.stringify(res));
          returnData = { checkStatus: -2, errMsg: '接口异常' };
        }
        if (opt.throwError && (!res || !res.url)) {
          throw new Error(returnData.errMsg);
        }
        return returnData;
      })
      .catch(async (err) => {
        let errMsg = '';
        try {
          if (err && err.statusCode !== undefined) {
            errMsg += `statusCode=${err.statusCode},`;
          }
          if (err && err.message !== undefined) {
            errMsg += `message=${err.message},`;
          }
          if (err && err.errMsg !== undefined) {
            errMsg += `errMsg=${err.errMsg},`;
          }
          if (err && err.code !== undefined) {
            errMsg += `code=${err.code},`;
          }
          errMsg = errMsg.replace(/,$/, '');
        } catch (error) {}
        logUploadImageFailInfo(err, errMsg);
        mwx.report(`api_image_upload_fail`, {
          time: mwx.formatLogTime(
            new Date() - beforeUploadTime - checkImageSafeTime
          ),
          errMsg
        });

        if (isInvalidPngError(err) && repeatIndex === 0 && !isLogFile) {
          try {
            var pngCheck = await mwx.detectImageIntegrity(file);
            if (pngCheck.isPng && !pngCheck.valid) {
              var fixed = await mwx.repairImageIntegrity(file);
              file = fixed.data;
              runResolveCallbackOnce();
              return checkImage(file, opt, repeatIndex + 1);
            }
          } catch (repairErr) {
            mwx.log.error('invalid_png repair fail', repairErr);
          }
        }

        if (opt.throwError) {
          runResolveCallbackOnce();
          throw err;
        }
        !isLogFile && mwx.uploadLogFile();

        mwx.log.error('checkImage fail:' + JSON.stringify(err));
        if (
          JSON.stringify(err).indexOf('empty_file') > -1 &&
          repeatIndex === 0
        ) {
          isNeedBase64Upload = true;
          runResolveCallbackOnce();
          return checkImage(file, opt, repeatIndex + 1);
        }
        runResolveCallbackOnce();
        if (err) {
          if (err.code == 1009) {
            return { checkStatus: -1, errMsg: `code=1009`, error: err };
          } else if (err.statusCode == 413) {
            return { ...err, checkStatus: -2, errMsg, error: err };
          } else {
            if (typeof err == 'string') {
              // 后台接口返回的错误信息在data字段中
              err = {
                data: err
              };
            }
            return { checkStatus: -2, errMsg, error: err };
          }
        } else {
          return { checkStatus: -2, errMsg, error: err };
        }
      });
  } catch (error) {
    runResolveCallbackOnce();
    logUploadImageFailInfo(error, error && error.message);
    !isLogFile && mwx.uploadLogFile();
    throw error;
    // 处理错误
  }
}

/**
 * 上传图片并检测
 * @param {*} file   file对象, base64字符串, blobUrl字符串
 * @param {*} [opt] - 同 _checkImage；首次上传 disableErrorToast 强制 false，重试时恢复调用方原值
 * @param {number} [repeatIndex=0]
 * @return {*}
 */
export async function checkImage(file, opt = {}, repeatIndex = 0) {
  var uploadOpt = Object.assign({}, opt);
  if (repeatIndex === 0) {
    uploadOpt.disableErrorToast = false;
  }
  return _checkImage(file, uploadOpt, repeatIndex);
}

/**
 * 查询图片安全检测结果
 * @param {Object} params - 查询参数
 * @param {string} [params.fileName] - 图片文件名（有 fileName 时使用 v5ImageCheckAsync）
 * @param {string} [params.mediaId] - 媒体ID（有 mediaId 时使用 v5MediaCheckAsync）
 * @returns {Promise} 返回检测结果的 Promise，与 checkImageSafe 返回格式一致
 */
export async function queryImageSafeCheck({ fileName, mediaId }) {
  let cacheKey = getSafeCheckCacheKey(fileName, mediaId);

  // 首先从 sessionStorage 读取已存储的结果
  let cachedResult = Store.getSession(cacheKey);
  if (
    cachedResult !== undefined &&
    cachedResult !== null &&
    isCacheCheckImage
  ) {
    return Promise.resolve(cachedResult);
  }

  // 如果 sessionStorage 中没有，检查内存缓存（可能正在检测中）
  if (imageSafeCheckCache.has(cacheKey)) {
    // 返回已存在的 Promise，等待检测完成，10秒超时
    return Promise.race([
      imageSafeCheckCache.get(cacheKey).catch(() => {
        // 如果 Promise 被 reject，返回 -1
        return -1;
      }),
      new Promise((resolve) => {
        setTimeout(() => {
          resolve(-1);
        }, 10 * 1000);
      })
    ]);
  } else {
    // 如果缓存中不存在，说明任务未启动或已完成并已清除
    // 返回 -1 表示未找到检测结果
    return Promise.resolve(-1);
  }
}

/**
 * 清除图片安全检测缓存
 * @param {Object} [params] - 可选参数
 * @param {string} [params.fileName] - 指定清除的文件名
 * @param {string} [params.mediaId] - 指定清除的媒体ID
 * @param {boolean} [params.clearAll=false] - 是否清除所有缓存
 */
export function clearImageSafeCheckCache({
  fileName,
  mediaId,
  clearAll = false
} = {}) {
  if (clearAll) {
    // 清除所有缓存
    imageSafeCheckCache.clear();
  } else if (fileName || mediaId) {
    // 清除指定缓存
    let cacheKey = getSafeCheckCacheKey(fileName, mediaId);
    imageSafeCheckCache.delete(cacheKey);
    // 从 sessionStorage 中移除检测结果
    Store.removeSession(cacheKey);
  }
}

// https://www.showdoc.com.cn/1612231782571698/9960101930530353
export function v5ImageSegmentUrl(data, opt = {}) {
  // if (process.env.NODE_ENV == 'development') {
  //   return new Promise((resolve) => {
  //     resolve({
  //       url:
  //         '//elfinbook-test.oss-cn-shanghai.aliyuncs.com/ocr/20251031/224/15bbd6008ef8611a98b22952d5b5a637.jpeg?Expires=1761896592&OSSAccessKeyId=LTAICNPOgWg0xOHr&Signature=%2B96808gKGyr8lo6%2FEJ%2FjGUFDMv8%3D',
  //       fileName: '15bbd6008ef8611a98b22952d5b5a637'
  //     });
  //   });
  // }

  let segmentData = data || {};
  let fileName = typeof segmentData == 'object' ? segmentData.fileName : data;
  let mediaId =
    typeof segmentData == 'object' && segmentData.mediaId != null
      ? String(segmentData.mediaId)
      : '';
  let segmentType = 3;
  if (opt.segmentType) {
    segmentType = opt.segmentType;
  }
  let formData = {
    segmentType
  };
  let api;
  if (mediaId) {
    formData.mediaId = mediaId;
    api = `/v5/media/segment/url`;
  } else {
    formData.fileName = fileName;
    api = `/v5/image/segment/url`;
  }
  if (opt.crop) {
    formData.crop = opt.crop;
  }

  let cacheKey = `v5ImageSegmentUrl${JSON.stringify(formData)}`;
  if (mwx.cache.get(cacheKey)) {
    return mwx.cache.get(cacheKey);
  }
  return Ajax.post(api, handleFormData(formData)).then((r) => {
    if (mwx.isDev) {
      r.url = r.url.replace(/^https/, 'http');
    }
    // 存储 url-filename 映射
    storeUrlFilenameMap(r);
    mwx.cache.set(cacheKey, r);
    return r;
  });
}
export function cardFaceDetect(data) {
  return Ajax.post('/v5/card/face/detect', handleFormData(data));
}

export async function cardFaceCrop() {}

export async function cardFaceScore(data) {
  return Ajax.post('/v5/card/face/score', handleFormData(data));
}
export async function cardFaceEnhance(data) {
  return Ajax.post('/v5/card/face/enhance', handleFormData(data));
}

export async function wechatConfig() {
  return Ajax.get(`/wechat/config`, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  });
}

export function v5ImageCheckAsync(data, opt) {
  return Ajax.post('/v5/image/check/async', handleFormData(data));
}

export function v5MediaCheckAsync(data, opt) {
  return Ajax.post('/v5/media/check/async', handleFormData(data));
}

export function webUser() {
  return Ajax.get('/web/user');
}

export function cardFaceBeautyAsync(data, opt = {}) {
  return Ajax.post('/v5/card/face/beauty/async', handleFormData(data)).then(
    (r) => {
      async function fn(timer) {
        let res;
        try {
          res = await cardFaceTask(
            {
              fileName: data.fileName
            },
            {
              disableErrorToast: true
            }
          );
          timer && clearInterval(timer);
          return res.url;
        } catch (error) {
          if (error && error.code == 1009) {
            console.log('图片检测1009：', r.fileName);
            timer && clearInterval(timer);
            throw error;
          } else {
            return -1;
          }
        }
      }

      return new Promise(async (resolve, reject) => {
        let timer2 = setTimeout(() => {
          clearInterval(timer);
          reject('timeout');
        }, 20000);
        let timer = setInterval(async () => {
          try {
            let t = await fn(timer);
            if (t != -1) {
              clearInterval(timer2);
              resolve({ data: t, key: opt.key });
            }
          } catch (error) {
            reject(error);
          }
        }, 500);
        // getApp().globalData.cardFaceTaskTimer = timer;
        try {
          let t = await fn(timer);
          if (t != -1) {
            resolve({ data: t, key: opt.key });
          }
        } catch (error) {
          reject(error);
        }
      });
    }
  );
}

function cardFaceTask(data, opt) {
  return Ajax.get('/v5/card/face/task?fileName=' + data.fileName, {
    extraParam: opt
  });
}

export function cardMask(data) {
  return Ajax.post('/v5/card/mask', handleFormData(data));
}

// https://www.showdoc.com.cn/2272359968911745/10567092863645795
export function ecOrderDetail(data) {
  let dyData = mwx.getDouyinData();
  let wxData = mwx.getWxData();
  let isDouyinApp = dyData && dyData.orderNo; // 是否在抖音小程序中
  let isWxApp = wxData && wxData.token; // 是否在微信小程序中
  let isUploadFormMiniAppSku = isWxApp && wxData.itemId && wxData.skuId; // 微信小程序接口变更
  let isOid = Store.getSession('isOid') == 1;
  let api = isOid ? '/v5/ec/order/detail/oid' : '/v5/ec/order/detail';
  if (isDouyinApp) {
    api = `/v5/ec/doudian/order/detail`;
    data = {
      orderNo: dyData.orderNo,
      productId: dyData.productId,
      skuId: dyData.skuId
    };
  } else {
    if (isOid) {
      data = {
        oId: data.soId
      };
    } else {
      data = {
        soId: data.soId
      };
    }
  }
  let func;
  if (isUploadFormMiniAppSku) {
    data = {
      itemId: wxData.itemId,
      skuId: wxData.skuId
    };
    if (wxData.gcId) {
      func = Ajax.get(`/v5/gc/detail?gcId=${wxData.gcId}`);
    } else {
      func = Ajax.get(
        `/v5/xb/sku/detail?itemId=${wxData.itemId}&skuId=${wxData.skuId}`
      );
    }
  } else if (wxData && wxData.orderNo) {
    func = Ajax.get(`/v5/xb/order/custom/detail?orderNo=${wxData.orderNo}`);
  } else {
    func = Ajax.post(api, handleFormData(data));
  }
  return func.then((r) => {
    if (isDouyinApp) {
      let skuItem = {};
      [
        'skuId',
        'outerOiId',
        'qty',
        'pic',
        'name',
        'propertiesValue',
        'template',
        'skuInfo'
      ].forEach((key) => {
        skuItem[key] = r[key];
      });
      r.skuList = [skuItem];
      r.locked = r.orderStatus == 10;
      r.goodsDetail = [
        {
          outerOiId: r.outerOiId,
          list: [...r.list]
        }
      ];
      dyData.shopId = r.shopId;
      mwx.setDouyinData(dyData);
    }
    if (isUploadFormMiniAppSku) {
      let template = mergeTemplate(r.template, r.skuInfo);
      if (wxData.gcId) {
        addTemplateType(template);
        r.skuDetail = {
          template: mwx.deepCopy(template),
          skuId: r.skuId,
          qty: 1
        };
        r.goodsDetail = [
          {
            outerOiId: wxData.gcId,
            list: r.list
          }
        ];
        r.skuList = [r.skuDetail];
      } else {
        addTemplateType(r);
        r.skuDetail = {
          template: mwx.deepCopy(r),
          skuId: r.skuId,
          qty: 1
        };
        r.goodsDetail = [];
        r.skuList = [r.skuDetail];
      }
    } else {
      r.skuList.forEach((v) => {
        let template = mergeTemplate(v.template, v.skuInfo);
        addTemplateType(template);
      });
    }
    try {
      let isNewSku =
        r.list.filter((v) => v.imageInfo && v.imageInfo.layers).length > 0;
      if ((r.version == 0 && isNewSku) || (r.version == 1 && !isNewSku)) {
        r.skuDetail.template.isSkuChange = true;
      }
    } catch (error) {}
    return r;
  });
}

function mergeTemplate(template, skuInfo) {
  if (skuInfo) {
    Object.keys(skuInfo).forEach((key) => {
      if (skuInfo[key] !== undefined) {
        template[key] = skuInfo[key];
      }
    });
  }
  return template;
}

// https://www.showdoc.com.cn/2272359968911745/10567073441888170
export function cardTemplate(hot = 0) {
  return Ajax.get('/v5/card/template?hot=', hot);
}

// https://www.showdoc.com.cn/2272359968911745/10572329788194777
export function orderUpdate(data) {
  let dyData = mwx.getDouyinData();

  if (mwx.isDev) {
    data = JSON.parse(JSON.stringify(data).replace(/http:\/\//g, 'https://'));
  }

  if (dyData && dyData.orderNo) {
    // 调用抖音接口
    let douyinData = {
      shopId: dyData.shopId,
      list: data.goodsDetail[0].list,
      outerOiId: data.goodsDetail[0].outerOiId
    };

    return Ajax.post('/v5/ec/doudian/order/update', douyinData, {
      headers: {
        'Content-Type': 'application/json'
      }
    });
  }
  data.goodsDetail.forEach((item) => {
    if (item.list.filter((v) => v.imageInfo).length > 0) {
      item.version = 1;
    } else {
      item.version = 0;
    }
  });

  return Ajax.post('/v5/ec/order/update', data, {
    headers: {
      'Content-Type': 'application/json'
    }
  });
}

export async function updateOrderData({
  addData,
  outerOiId,
  soId,
  addToLast = false
}) {
  let r = await ecOrderDetail({ soId });
  let skuItem = r.skuList.filter((v) => v.outerOiId == outerOiId)[0];
  let item = r.goodsDetail.filter((v) => v.outerOiId === outerOiId)[0];
  if (item) {
    let count = 0;
    item.list.forEach((v) => {
      count += v.qty * v.imageUrlList.length;
    });
    if (count + 1 <= skuItem.qty * skuItem.template.bgCount) {
      if (addToLast) {
        item.list = [...item.list, addData];
      } else {
        item.list = [addData, ...item.list];
      }
    } else {
      return -1;
    }
  } else {
    item = {
      outerOiId,
      list: [addData]
    };
    r.goodsDetail = [item, ...r.goodsDetail];
  }

  return orderUpdate(r);
}

// https://www.showdoc.com.cn/2272359968911745/10572336148972942
export function orderPrint(ioId, data, soId) {
  let dyData = mwx.getDouyinData();
  if (dyData && dyData.orderNo) {
    // 调用抖音接口
    let douyinData = {
      shopId: dyData.shopId,
      orderNo: dyData.orderNo
    };
    return Ajax.post('/v5/ec/doudian/order/print', handleFormData(douyinData));
  }
  let wxData = mwx.getWxData();
  let api = `/v5/ec/order/print?ioId=${ioId}`;
  if (wxData) {
    api = `/v5/xb/order/print?ioId=${ioId}&soId=${soId}`;
  }
  return Ajax.post(api, data, {
    headers: {
      'Content-Type': 'application/json'
    }
  });
}

export function siteVisitorLogin(data) {
  if (isMockData) {
    return Promise.resolve(mockdata.loginData);
  }
  return Ajax.post(
    `/v5/ec/order/login?deviceId=${data.deviceId}&soId=${data.soId}`,
    {
      timeout: 10 * 1000
    }
  );
}

export function wechatUrl(data) {
  return Ajax.post(`/wechat/url?platform=${platform}`, handleFormData(data), {
    disableErrorToast: true
  });
}

function storePldSizeInfo(data) {
  try {
    let info = data.list[0].list[0];
    mwx.Store.set('pldSizeInfo', info);
  } catch (error) {}
}

let templateCache = {};
export function cardPolaroidTemplate({ skuType = 3, skuId }) {
  let key = `skuType=${skuType},skuId=${skuId}`;
  if (templateCache[key]) {
    storePldSizeInfo(templateCache[key]);
    return Promise.resolve(templateCache[key]);
  }
  return Ajax.get(
    `/v5/card/polaroid/template?skuType=${skuType}&skuId=${skuId}`
  ).then((r) => {
    if (mwx.isDev && !isDebuggerProduction) {
      r.list.forEach((v) => {
        v.list.forEach((res) => {
          res.bigImageUrl = res.bigImageUrl.replace(/^https/, 'http');
          res.imageUrl = res.imageUrl.replace(/^https/, 'http');
          res.thumbImageUrl = res.thumbImageUrl.replace(/^https/, 'http');
        });
      });
    }

    templateCache[key] = r;
    storePldSizeInfo(r);
    return r;
  });
}

export function wechatLog(data) {
  let soId = Store.getSession('soId') || '';
  return Ajax.post(
    `/wechat/log?soId=${soId}&mediaId=${data.mediaId}&warning=${
      data.warning || 0
    }&logCategory=${data.logCategory || ''}`
  );
}

/**
 * 安全解析后台返回的拼图模板字段。
 * @param {string|Array} template - 后台返回的模板字段。
 * @returns {Array} 模板数组；解析失败时返回空数组。
 */
function parseLifeTemplate(template) {
  // 后台也可能直接返回数组，直接复用。
  if (Array.isArray(template)) {
    return template;
  }
  try {
    const parsed = JSON.parse(template || '[]');
    // 模板字段必须是数组，非法结构按空模板兜底。
    if (Array.isArray(parsed)) {
      return parsed;
    }
  } catch (error) {
    console.error('convertLifeTemplateList: template 解析失败', error);
  }
  return [];
}

// 转换后台返回的模板数据格式：https://www.showdoc.com.cn/2272359968911745/11416483408530180
// 转换为同 creativeTemplate的格式
function convertLifeTemplateList(list) {
  let r = list.map((item) => {
    let template = parseLifeTemplate(item.template);
    return {
      ...item,
      bg: item.templateImageUrl,
      bgColor: item.bgColor,
      templateImageSrc: item.templateImageUrl,
      ratio: item.imageWidth / item.imageHeight,
      itemList: template.map((v) => {
        return {
          left: (v[0] / item.imageWidth) * 100,
          top: (v[1] / item.imageHeight) * 100,
          percent_width: (v[2] / item.imageWidth) * 100,
          percent_height: (v[3] / item.imageHeight) * 100
        };
      }),
      imageCount: item.imageCount,
      puzzleId: item.puzzleId,
      template,
      imageWidth: item.imageWidth,
      imageHeight: item.imageHeight
    };
  });
  return r;
}

export function v5EcPuzzleTemplate(skuType = 0) {
  let cacheKey = '/v5/ec/puzzle/template?skuType=' + skuType;
  return cacheApi(cacheKey, () =>
    Ajax.get(cacheKey).then((r) => {
      let res = convertLifeTemplateList(r.list);
      return res;
    })
  );
}

export function v5EcPuzzleTemplateV2(skuType = 0) {
  let cacheKey = '/v5/ec/puzzle/template/v2?skuType=' + skuType;
  return cacheApi(cacheKey, () =>
    Ajax.get(cacheKey).then((r) => {
      r.list.forEach((v) => {
        v.list = convertLifeTemplateList(v.list);
      });
      return r.list;
    })
  );
}

export function v5MediaCrop(data) {
  let cacheKey = '/v5/media/crop' + JSON.stringify(data);
  return cacheApi(cacheKey, () =>
    Ajax.post(`/v5/media/crop`, handleFormData(data), {
      disableErrorToast: true
    })
  );
}

export function v5ImageCrop(data) {
  let cacheKey = '/v5/image/crop' + JSON.stringify(data);
  return cacheApi(cacheKey, () =>
    Ajax.post(`/v5/image/crop`, handleFormData(data), {
      disableErrorToast: true
    })
  );
}

export function v5MediaRotate(data) {
  let cacheKey = '/v5/media/rotate' + JSON.stringify(data);
  return cacheApi(cacheKey, () =>
    Ajax.post(`/v5/media/rotate`, handleFormData(data), {
      disableErrorToast: true
    })
  );
}

export function v5ImageRotate(data) {
  let cacheKey = '/v5/image/rotate' + JSON.stringify(data);
  return cacheApi(cacheKey, () =>
    Ajax.post(`/v5/image/rotate`, handleFormData(data), {
      disableErrorToast: true
    })
  );
}

export function v5MediaCopy(data) {
  let cacheKey = '/v5/media/copy' + JSON.stringify(data);
  return cacheApi(cacheKey, () =>
    Ajax.post(`/v5/media/copy`, handleFormData(data), {
      disableErrorToast: true
    })
  );
}

export function v5ImageDetail(data) {
  let cacheKey = '/v5/image/detail' + JSON.stringify(data);
  return cacheApi(cacheKey, () =>
    Ajax.get(`/v5/image/detail?fileName=${data.fileName}`, {
      disableErrorToast: true
    }).then((r) => {
      r.url = r.url.replace(/^https/, 'http');
      return r;
    })
  );
}

export function imageBorderParse(data) {
  let cacheKey = '/v5/image/border/parse' + JSON.stringify(data);
  if (data.slotRect && data.slotRect[4] === undefined) {
    let template = mwx.Store.get(mwx.constant.skuTemplate) || {};
    data.slotRect[4] = template.slotWidth;
    data.slotRect[5] = template.slotHeight;
  }
  return cacheApi(cacheKey, () =>
    Ajax.post(
      `/v5/image/border/parse/json`,
      { ...data, maskDetail: 1 },
      {
        headers: {
          'Content-Type': 'application/json'
        }
      }
    )
  );
}

export function imageCropAlpha(data) {
  let startTime = new Date();
  let cacheKey = '/v5/image/crop/alpha' + JSON.stringify(data);
  return cacheApi(cacheKey, () =>
    Ajax.post(`/v5/image/crop/alpha`, handleFormData(data))
  ).then((r) => {
    // 存储 url-filename 映射
    storeUrlFilenameMap(r);
    mwx.logTime('imageCropAlpha耗时', new Date() - startTime);
    return r;
  });
}

export function imageBorder(data) {
  if (!data.mask || data.mask === 'undefined') {
    data.mask = '';
  }
  let cacheKey = '/v5/media/border' + JSON.stringify(data);
  let d = JSON.parse(JSON.stringify(data));
  d.maskDetail = 1;
  if (!d.mask || d.mask === undefined || d.mask === 'undefined') {
    delete d.mask;
  }
  return cacheApi(cacheKey, () =>
    Ajax.post(`/v5/media/border`, handleFormData(d))
  );
}

export function getShareList() {
  let cacheKey = '/v5/ec/shape';
  return cacheApi(cacheKey, () => Ajax.get(`/v5/ec/shape`));
}

export function v5XbSkuDetail(data) {
  let cacheKey = '/v5/xb/sku/detail' + JSON.stringify(data);
  return cacheApi(cacheKey, () =>
    Ajax.get(
      `/v5/xb/sku/detail?skuId=${data.skuId || ''}&itemId=${data.itemId || ''}`
    )
  );
}

export function v5XbOrderCustomDetail(data) {
  let cacheKey = '/v5/xb/order/custom/detail' + JSON.stringify(data);
  return cacheApi(cacheKey, () =>
    Ajax.get(`/v5/xb/order/custom/detail?orderNo=${data.orderNo || ''}`)
  );
}

export function getMiniSetting() {
  return Ajax.get('/wechat/config').then((res) => {
    try {
      let config = JSON.parse(res.config);
      res.config = config;
    } catch (error) {}
    return res;
  });
}

export function v5EcColorPaper(data) {
  let cacheKey = '/v5/ec/color/paper' + JSON.stringify(data);
  return cacheApi(cacheKey, () =>
    Ajax.get(`/v5/ec/color/paper?skuId=${data.skuId || ''}`)
  );
}

// https://www.showdoc.com.cn/2598836297328203/11558961663790750
export function v5EcBadgeList(data) {
  let cacheKey = '/v5/ec/badge/list' + JSON.stringify(data);
  return cacheApi(cacheKey, () =>
    Ajax.get(`/v5/ec/badge/list?skuId=${data.skuId || ''}`)
  );
}

/**
 * 设计素材列表（背景/普通素材/烫色素材等）
 * @param {Object} data
 * @param {string} [data.designType='bg'] 素材类型：背景 bg，普通素材 icon，烫色素材 foil
 * @returns {Promise<{list: Array<{categoryName: string, list: Array}>}>}
 */
export function v5EcDesign(data = {}) {
  const designType = data.designType || 'bg';
  const cacheKey = '/v5/ec/design' + JSON.stringify({ ...data, designType });
  return cacheApi(cacheKey, () =>
    Ajax.get(`/v5/ec/design?designType=${designType}`).then((res) => {
      // v5/ec/design 也可能返回文字素材字体列表，需要与 template 字体列表合并缓存。
      if (res && Array.isArray(res.fontList)) {
        saveImageDataFontListToStore(res.fontList);
      }
      return res;
    })
  );
}

export function gcCreate(data) {
  return Ajax.post(`/v5/gc/create`, data, {
    headers: {
      'Content-Type': 'application/json'
    }
  });
}

export function v5GcDetail(data) {
  return Ajax.get(`/v5/gc/detail?gcId=${data.gcId || ''}`);
}

export function v5EcTemplate(data) {
  let cacheKey = '/v5/ec/template' + JSON.stringify(data);
  return cacheApi(cacheKey, () =>
    Ajax.get(`/v5/ec/template?skuId=${data.skuId || ''}`).then((res) => {
      // v5/ec/template 是图片编辑文字字体列表的源头，首次返回后写入全局 Store。
      if (res && Array.isArray(res.fontList)) {
        saveImageDataFontListToStore(res.fontList);
      }
      res.list.forEach((v) => {
        v.list.forEach((j) => {
          j.maskUrl = res.maskUrl;
        });
      });
      mwx.setCurrentOuterOiIdPhysicalMaskUrl(res.physicalMaskUrl);
      return res;
    })
  );
}

export function v5MediaWhite(data) {
  let cacheKey = '/v5/media/white' + JSON.stringify(data);
  const url = `/v5/media/white?${queryString(data)}`;
  return cacheApi(cacheKey, () => Ajax.post(url, data, {}));
}

export function v5ImageWhite(data) {
  let cacheKey = '/v5/image/white' + JSON.stringify(data);
  const url = `/v5/image/white?${queryString(data)}`;
  return cacheApi(cacheKey, () => Ajax.post(url));
}

// https://app.apifox.com/project/7389798%E6%A0%B7%E6%9C%BA/%E6%A0%B7%E6%9C%BA%E5%88%97%E8%A1%A8
export function v5EcMockUp() {
  let cacheKey = '/v5/ec/mockup';
  const url = `/v5/ec/mockup`;
  return cacheApi(cacheKey, () => Ajax.get(url));
}

export function elfinH5JsSdk(data) {
  return Ajax.post(
    `/site/jssdk?url=${encodeURIComponent(location.href.split('#')[0])}`
  );
}

export function v5EcLamination(data) {
  let cacheKey = '/v5/ec/lamination' + JSON.stringify(data);
  return cacheApi(cacheKey, () =>
    Ajax.get(`/v5/ec/lamination?skuId=${data.skuId || ''}`)
  );
}

export function ImageUploadUrl(data) {
  const fileUrl = data.file || '';
  const filePath = fileUrl.split('?')[0].split('#')[0];
  let fileName = filePath.split('/').pop();
  try {
    fileName = decodeURIComponent(fileName);
  } catch (error) {}
  // 未从 http 链接中解析到文件名时，用时间戳图片名兜底。
  if (!fileName) {
    fileName = Date.now() + '.png';
  }
  data.fileName = fileName;
  data.poi = 306;
  data.source = 2;
  return Ajax.post(`/v5/image/upload/url`, data, {
    headers: {
      'Content-Type': 'application/json'
    }
  });
}
